import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
export interface LegalDocument {
  title: string;
  lastUpdated: string;
  sections: {
    heading: string;
    text: string;
  }[];
}
interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: LegalDocument | null;
}
export function LegalModal({
  isOpen,
  onClose,
  document
}: LegalModalProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      window.document.body.style.overflow = 'hidden';
      // Focus close button on open for accessibility
      setTimeout(() => closeButtonRef.current?.focus(), 100);
    } else {
      window.document.body.style.overflow = 'unset';
    }
    return () => {
      window.document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  // Handle Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-52">
      {isOpen && document && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-53">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-54" />

          {/* Modal Panel */}
          <motion.div role="dialog" aria-modal="true" aria-labelledby="modal-title" initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="relative w-full max-w-2xl max-h-[85vh] flex flex-col bg-[#0f0f1a] border border-brand-gold/30 rounded-2xl shadow-2xl overflow-hidden" data-id="element-55">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-brand-gold/10 bg-[#0f0f1a] z-10" data-id="element-56">
              <div data-id="element-57">
                <h2 id="modal-title" className="font-serif text-2xl md:text-3xl text-brand-gold" data-id="element-58">
                  {document.title}
                </h2>
                <p className="text-xs text-gray-400 mt-1 uppercase tracking-wider" data-id="element-59">
                  Última actualización: {document.lastUpdated}
                </p>
              </div>
              <button ref={closeButtonRef} onClick={onClose} className="p-2 text-gray-400 hover:text-brand-gold hover:bg-brand-gold/10 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-gold/50" aria-label="Cerrar modal" data-id="element-60">
                <X size={24} data-id="element-61" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-8 custom-scrollbar" data-id="element-62">
              {document.sections.map((section, idx) => <section key={idx} data-id="element-63">
                  <h3 className="text-lg font-medium text-gray-100 mb-3 tracking-wide" data-id="element-64">
                    {section.heading}
                  </h3>
                  <p className="text-base text-gray-300 font-light leading-relaxed" data-id="element-65">
                    {section.text}
                  </p>
                </section>)}
            </div>

            {/* Bottom Gradient Fade (Optional visual touch) */}
            <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[#0f0f1a] to-transparent pointer-events-none" data-id="element-66" />
          </motion.div>
        </div>}
    </AnimatePresence>;
}